// Витрина фигурок в любом образе (макет «Витрина · любой образ»).
import { useCatalog } from "../../app/queries";
import { SiteFooter } from "../../site/SiteFooter";
import { SiteHeader } from "../../site/SiteHeader";
import { caseImage, caseImageAlt, lightNote } from "../../site/cases";
import { casePriceLines, formatRub, priceOf, pricedCases } from "../../site/prices";
import { FAQ_PATH, GIFT_FAQ, GIFT_FAQ_PATH } from "../../site/faq";
import {
  FinalCall,
  Guarantee,
  HowItWorks,
  Questions,
  ShowcaseSection,
  StartButton,
} from "./ShowcaseParts";
import styles from "./Showcase.module.css";
import { SIZES_FULL, SIZES_HALF, SIZES_THUMB, picture } from "../../site/pictures";

// Примеры образов — обобщённые, без названий фильмов, игр и персонажей.
// Примеры образа подарка: где естественно — с позой или предметом, поле у подарка одно
// (test-run-fixes, 01.11.2026).
export const GIFT_LOOK_EXAMPLES = [
  "пират с саблей",
  "космический десантник",
  "рыцарь с мечом",
  "супергерой в полёте",
  "принцесса-воительница",
  "повар с половником",
  "рок-звезда с гитарой",
];

const SIZES = [
  { id: "gift_10", title: "10 см" },
  { id: "gift_15", title: "15 см" },
];

export function ShowcaseGift() {
  const { data: catalog } = useCatalog();
  // Кейс и есть подарочная упаковка: отдельной коробки нет (Иван, 25.10.2026).
  const cases = pricedCases(catalog, "gift");
  const casePrices = casePriceLines(cases);
  const light = lightNote(cases);
  const firstCase = cases[0];
  const priceRows = SIZES.map((size) => ({
    ...size,
    grey: catalog ? priceOf(catalog, "gift", "print_grey", size.id) : null,
  })).filter((row) => row.grey !== null);
  return (
    <div className={`${styles.page} gift`}>
      <SiteHeader gift />
      <main className={styles.main}>
        <section className={styles.hero}>
          <p className="eyebrow" style={{ color: "var(--gift)" }}>
            Фигурка в любом образе
          </p>
          <h1 className="h1">Кем угодно — с твоим лицом</h1>
          <p className="text muted">
            Пират, рыцарь, повар — опиши образ словами. Украсит твой интерьер — или станет подарком, какого
            больше нет ни у кого.
          </p>
          <div className={styles.picture} style={{ aspectRatio: "4 / 3", marginTop: 4 }}>
            <img
              {...picture("/showcase/gift_shelf.jpg", SIZES_FULL, "lead")}
              alt="Фигурка пирата на книжной полке"
              style={{ objectPosition: "48% 40%" }}
            />
            <span className={`${styles.pictureTag} mono`} style={{ background: "var(--surface)", padding: "4px 8px", borderRadius: 999 }}>
              15 см
            </span>
          </div>
          <p className="text mono muted">Пример работы, лицо сгенерировано</p>
          <StartButton direction="gift" />
          <p className={`text small muted ${styles.centered}`}>Эскиз — бесплатно</p>
        </section>

        <ShowcaseSection title="Кем будешь?">
          <p className="text muted">
            Опиши образ своими словами — или начни с готового. Позу и детали можно попросить сразу.
          </p>
          <ul className={styles.chips} aria-label="Примеры образов">
            {GIFT_LOOK_EXAMPLES.map((example) => (
              <li key={example}>{example}</li>
            ))}
          </ul>
        </ShowcaseSection>

        {cases.length > 0 && (
          <ShowcaseSection title="В подарочном кейсе">
            <div className={styles.caseMosaic}>
              {cases.map((caseView) => (
                <figure key={caseView.kind}>
                  <img {...picture(caseImage(caseView.kind, "gift"), SIZES_HALF)} alt={caseImageAlt(caseView.kind, "gift")} />
                  <figcaption>{caseView.title_ru}</figcaption>
                </figure>
              ))}
            </div>
            <p className="text muted">
              {light !== null && `${light}: за фигуркой светится осколок, батарейка в комплекте, выключатель снизу. `}
              Вид кейса выбираешь при заказе.
            </p>
          </ShowcaseSection>
        )}

        <HowItWorks
          steps={[
            { title: "Сделай селфи", text: "Своё или того, кого хочешь порадовать. Для большего сходства добавь фото вполоборота и в профиль." },
            { title: "Опиши образ и пожелания", text: "Кто это и как стоит: «пират, держит саблю, улыбается»." },
            { title: "Посмотри фигурку", text: "Эскиз будет примерно через минуту." },
          ]}
        />

        <ShowcaseSection title="Работы" aside={<span className="mono muted">лица сгенерированы</span>}>
          <div className={styles.gallery}>
            <figure>
              <img {...picture("/showcase/example_concept_front.jpg", SIZES_HALF)} alt="Лучница в зелёном плаще" />
              <figcaption className="small" style={{ fontWeight: 600 }}>
                Лучница в зелёном плаще
              </figcaption>
            </figure>
            <figure>
              <img {...picture("/showcase/example_grey_front.jpg", SIZES_HALF)} alt="Она же в серой печати" />
              <figcaption className="small" style={{ fontWeight: 600 }}>
                Она же, серая печать
              </figcaption>
            </figure>
          </div>
          <p className="text small muted">Пример работы, лицо сгенерировано.</p>
        </ShowcaseSection>

        {priceRows.length > 0 && (
          <ShowcaseSection title="Цены">
            <div className={styles.rows} style={{ paddingBottom: 8 }}>
              <table className={styles.priceTable}>
                <thead>
                  <tr>
                    <th scope="col">Размер</th>
                    <th scope="col">Серая печать</th>
                  </tr>
                </thead>
                <tbody>
                  {priceRows.map((row) => (
                    <tr key={row.id}>
                      <th scope="row">{row.title}</th>
                      <td>{row.grey !== null ? formatRub(row.grey) : "—"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {firstCase !== undefined && (
              <div className={styles.extra}>
                <img {...picture(caseImage(firstCase.kind, "gift"), SIZES_THUMB)} alt="" style={{ width: 112 }} />
                <div className={styles.extraBody} style={{ flex: 1 }}>
                  <span style={{ fontWeight: 600 }}>Кейс</span>
                  <span className="small muted">цена одна для 10 и 15 см</span>
                  {casePrices.map((line, index) => (
                    <span key={line.titles} className={styles.casePriceRow} style={index === 0 ? { marginTop: 4 } : undefined}>
                      <span className="small">{line.titles}</span>
                      <span className="price" style={{ fontSize: 15 }}>
                        + {formatRub(line.price)}
                      </span>
                    </span>
                  ))}
                </div>
              </div>
            )}
          </ShowcaseSection>
        )}

        <Guarantee text="Перед печатью покажем 3D-модель. Не понравится — переделаем или вернём деньги." />

        <Questions items={GIFT_FAQ} allQuestionsPath={GIFT_FAQ_PATH} />
        <FinalCall direction="gift" slogan="Подари то, чего нет ни в одном магазине" />
      </main>
      <SiteFooter
        links={[
          { to: "/mini", label: "Миниатюра для игры" },
          { to: FAQ_PATH, label: "Вопросы" },
          { to: "/docs", label: "Документы" },
          { to: "/docs/refund", label: "Возврат" },
        ]}
      />
    </div>
  );
}
